import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Slider,
} from '@mui/material';
import ColorPickerPopover from './ColorPickerPopover';

const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
    <Typography variant="caption" sx={{ color: '#888', fontSize: 11, width: 56, flexShrink: 0 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const ColorRow = ({ label, value, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const hex = value && !value.startsWith('rgb') ? value : '#ffffff';

  return (
    <Row label={label}>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          width: 24, height: 24, borderRadius: 1,
          background: value || '#fff',
          border: '1px solid #ddd', cursor: 'pointer', flexShrink: 0,
        }}
      />
      <TextField
        size="small"
        value={hex}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ style: { fontSize: 12, padding: '3px 6px', fontFamily: 'monospace' } }}
        sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
      />
      <ColorPickerPopover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        value={hex}
        onChange={(c) => { onChange(c); setAnchor(null); }}
      />
    </Row>
  );
};

const FourSides = ({ label, values, onChange }) => {
  // values: { top, right, bottom, left } or a single string like "16px"
  const parse = (v) => parseFloat(v) || 0;
  const flat = typeof values === 'string';
  const top = flat ? parse(values) : parse(values?.top);
  const right = flat ? parse(values) : parse(values?.right);
  const bottom = flat ? parse(values) : parse(values?.bottom);
  const left = flat ? parse(values) : parse(values?.left);

  const sides = [
    { key: 'top', val: top },
    { key: 'right', val: right },
    { key: 'bottom', val: bottom },
    { key: 'left', val: left },
  ];

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ color: '#888', fontSize: 11, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
        {sides.map(({ key, val }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#bbb', fontSize: 10, width: 24 }}>{key[0].toUpperCase()}</Typography>
            <TextField
              size="small"
              type="number"
              value={val}
              onChange={(e) => onChange(key, e.target.value + 'px')}
              inputProps={{ min: 0, style: { fontSize: 11, padding: '2px 4px', width: 40 } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const BoxPanel = ({ styles = {}, onChange }) => {
  const get = (prop, fallback = '') => styles[prop] || fallback;

  const opacity = Math.round((parseFloat(get('opacity', '1')) || 1) * 100);

  const handlePaddingChange = (side, value) => {
    const propMap = {
      top: 'paddingTop',
      right: 'paddingRight',
      bottom: 'paddingBottom',
      left: 'paddingLeft',
    };
    onChange(propMap[side], value);
  };

  const handleMarginChange = (side, value) => {
    const propMap = {
      top: 'marginTop',
      right: 'marginRight',
      bottom: 'marginBottom',
      left: 'marginLeft',
    };
    onChange(propMap[side], value);
  };

  const paddingValues = {
    top: get('paddingTop', get('padding', '0px')),
    right: get('paddingRight', get('padding', '0px')),
    bottom: get('paddingBottom', get('padding', '0px')),
    left: get('paddingLeft', get('padding', '0px')),
  };

  const marginValues = {
    top: get('marginTop', get('margin', '0px')),
    right: get('marginRight', get('margin', '0px')),
    bottom: get('marginBottom', get('margin', '0px')),
    left: get('marginLeft', get('margin', '0px')),
  };

  return (
    <Box>
      {/* Fill color */}
      <ColorRow
        label="Fill"
        value={get('backgroundColor', '#ffffff')}
        onChange={(v) => onChange('backgroundColor', v)}
      />

      {/* Opacity */}
      <Row label="Opacity">
        <Slider
          size="small"
          value={opacity}
          onChange={(_, v) => onChange('opacity', String(v / 100))}
          min={0}
          max={100}
          sx={{ flex: 1, color: '#1a1a1a' }}
        />
        <Typography variant="caption" sx={{ fontSize: 11, width: 28, textAlign: 'right' }}>
          {opacity}%
        </Typography>
      </Row>

      {/* Border radius */}
      <Row label="Radius">
        <TextField
          size="small"
          type="number"
          value={parseFloat(get('borderRadius', '0px')) || 0}
          onChange={(e) => onChange('borderRadius', e.target.value + 'px')}
          inputProps={{ min: 0, style: { fontSize: 12, padding: '3px 6px', width: 52 } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />
      </Row>

      {/* Border */}
      <Row label="Border">
        <TextField
          size="small"
          value={get('border', 'none')}
          onChange={(e) => onChange('border', e.target.value)}
          placeholder="1px solid #ddd"
          inputProps={{ style: { fontSize: 12, padding: '3px 6px' } }}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />
      </Row>

      {/* Padding */}
      <FourSides label="Padding" values={paddingValues} onChange={handlePaddingChange} />

      {/* Margin */}
      <FourSides label="Margin" values={marginValues} onChange={handleMarginChange} />
    </Box>
  );
};

export default BoxPanel;
